import { useState, useEffect, useRef } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, ArrowRight, Star, Loader2 } from 'lucide-react'
import { useAnnouncement } from '../hooks/useAnnouncement'
import { SURVEY_URL, npsAnnouncementContent } from '../lib/announcement'
import { submitAnnouncementNps } from '../lib/api'
import headerImage from '../assets/announcement-header.jpg'

/**
 * Survey-invite announcement modal. Captures a 1–5 star rating inline first
 * (one tap, high response rate), then invites the engaged user to the full
 * survey.
 *
 * The dark-navy hero image (the glowing chat-bubble icon) is a fixed-height
 * header banner; the body below uses the platform theme tokens (bg-card /
 * text-foreground), so the modal is light in the light theme and dark in the
 * dark theme — matching the rest of the dashboard. Auto-shows once when live,
 * and reopens when the user clicks the bell notification (see useAnnouncement +
 * notificationLinks.ts).
 */

/** Shared modal shell: hero banner + themed body + close button. The
 * Dialog.Close button drives the close via Dialog.Root's onOpenChange. */
function AnnouncementShell({ children }: { children: React.ReactNode }) {
  return (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
      <Dialog.Content
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100%-1.5rem)] max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-2xl border border-border bg-card text-card-foreground shadow-xl data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
      >
        {/* Hero banner: the glowing chat-bubble image. Always dark navy (it's an
            image), so it reads as a branded header in both themes. In the dark
            theme its near-black bottom fades smoothly into the (dark) card; the
            light theme uses a crisp banner edge instead, because fading navy
            into a white card looks muddy. */}
        <div
          style={{ backgroundImage: `url(${headerImage})` }}
          className="relative h-32 sm:h-40 bg-cover bg-top bg-no-repeat"
        >
          {/* Fade only in dark mode (where the card is also dark navy). */}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-card hidden dark:block" />

          {/* Close button floats over the (dark) banner, so light-on-dark
              styling is correct in both themes. */}
          <Dialog.Close asChild>
            <button
              className="absolute right-3 top-3 z-20 p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </Dialog.Close>
        </div>

        {/* Themed body. -mt overlaps the banner's dark-mode fade; in light mode
            it just tightens the gap under the crisp banner edge. */}
        <div className="relative z-10 flex flex-col items-center text-center px-6 sm:px-7 dark:-mt-4 pt-5 pb-6">
          {children}
        </div>
      </Dialog.Content>
    </Dialog.Portal>
  )
}

/** Two secondary escape buttons: soft snooze ("Maybe later") + terminal
 * opt-out for this round ("I've done it"). */
function EscapeRow({
  onSnooze,
  onDone,
  deferLabel,
  doneLabel,
}: {
  onSnooze: () => void
  onDone: () => void
  deferLabel: string
  doneLabel: string
}) {
  return (
    <div className="mt-4 flex items-center justify-center gap-1 text-sm">
      <button
        onClick={onSnooze}
        className="rounded-lg px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      >
        {deferLabel}
      </button>
      <span className="text-border" aria-hidden="true">
        ·
      </span>
      <button
        onClick={onDone}
        className="rounded-lg px-3 py-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
      >
        {doneLabel}
      </button>
    </div>
  )
}

function NpsBody({
  initialRating,
  onTakeSurvey,
  onSnooze,
  onDone,
}: {
  /** The user's already-submitted rating for this round, if any — opens the
   * modal in its "thanks" state with the stars filled instead of re-asking. */
  initialRating: number | null
  onTakeSurvey: () => void
  onSnooze: () => void
  onDone: () => void
}) {
  const [rating, setRating] = useState<number | null>(initialRating)
  const [hovered, setHovered] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const touchedRef = useRef(false)
  const rated = rating !== null

  // The prior rating may arrive just after the modal opens (the bell/self-send
  // paths open immediately, then fetch state). Reflect it once it loads —
  // unless the user has already tapped a star this session.
  useEffect(() => {
    if (!touchedRef.current && initialRating !== null) setRating(initialRating)
  }, [initialRating])

  async function handleRate(value: number) {
    // Re-rating is allowed — tapping a different star overwrites the previous
    // rating (the backend upserts per user+epoch). Only block while a submit
    // is in flight, and skip a no-op re-tap of the same value.
    if (submitting || value === rating) return
    touchedRef.current = true
    setRating(value)
    setSubmitting(true)
    try {
      await submitAnnouncementNps(value)
    } catch {
      // Non-critical — keep the optimistic rating; the survey link still works.
    } finally {
      setSubmitting(false)
    }
  }

  const active = hovered ?? rating ?? 0

  return (
    <>
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
        {npsAnnouncementContent.eyebrow}
      </p>

      <div className="mt-3 mb-2 h-px w-16 bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />

      <Dialog.Title className="text-2xl sm:text-3xl font-bold leading-tight text-foreground">
        {rated ? npsAnnouncementContent.thanksHeadline : npsAnnouncementContent.headline}
      </Dialog.Title>

      <Dialog.Description className="mt-3 text-sm leading-relaxed text-muted-foreground max-w-xs">
        {rated ? npsAnnouncementContent.thanksBody : npsAnnouncementContent.body}
      </Dialog.Description>

      {/* Star row */}
      <div
        className="mt-6 flex items-center justify-center gap-1.5"
        role="radiogroup"
        aria-label="Rate your experience from 1 to 5 stars"
        onMouseLeave={() => setHovered(null)}
      >
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={rating === value}
            aria-label={`${value} star${value === 1 ? '' : 's'}`}
            disabled={submitting}
            onMouseEnter={() => setHovered(value)}
            onClick={() => handleRate(value)}
            className="p-1 transition-transform enabled:hover:scale-110 disabled:cursor-default"
          >
            <Star
              className={
                value <= active
                  ? 'w-8 h-8 fill-amber-400 text-amber-400'
                  : 'w-8 h-8 text-muted-foreground/40'
              }
            />
          </button>
        ))}
      </div>

      {/* Before rating: just the prompt (the X dismisses). The full-survey CTA
          and the "Maybe later" / "I've done it" buttons all refer to the FULL
          survey, so they only appear once a star is tapped and that link shows. */}
      {!rated ? (
        <p className="mt-2 text-xs text-muted-foreground">{npsAnnouncementContent.ratePrompt}</p>
      ) : (
        <>
          <button
            onClick={onTakeSurvey}
            className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-900/40 transition-colors hover:from-blue-500 hover:to-blue-400"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {npsAnnouncementContent.ctaLabel}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>

          <EscapeRow
            onSnooze={onSnooze}
            onDone={onDone}
            deferLabel={npsAnnouncementContent.deferLabel}
            doneLabel={npsAnnouncementContent.doneLabel}
          />
        </>
      )}
    </>
  )
}

export function AnnouncementDialog() {
  const { showDialog, surveyUrl, npsRating, markDone, snooze } = useAnnouncement()

  function handleOpenChange(open: boolean) {
    // Closing via overlay click / Esc / X is a SOFT dismiss → snooze (counts
    // toward the show cap), not a terminal "done". "I've done it" is terminal.
    if (!open) snooze('close_x')
  }

  function handleTakeSurvey() {
    // Taking the survey is terminal for this round. Admin-configured link if
    // set, else the built-in default.
    window.open(surveyUrl || SURVEY_URL, '_blank', 'noopener,noreferrer')
    markDone('take_survey')
  }

  // Wrap in arg-less closures so the button click event isn't passed as `source`.
  const onMaybeLater = () => snooze('maybe_later')
  const onImDone = () => markDone('done')

  return (
    <Dialog.Root open={showDialog} onOpenChange={handleOpenChange}>
      <AnnouncementShell>
        <NpsBody
          initialRating={npsRating}
          onTakeSurvey={handleTakeSurvey}
          onSnooze={onMaybeLater}
          onDone={onImDone}
        />
      </AnnouncementShell>
    </Dialog.Root>
  )
}
